import { useEffect, type DependencyList } from 'react'

// ── Event categories ──────────────────────────────────────────────────────────
export type MoriEventCategory =
  | 'card'
  | 'memory'
  | 'challenge'
  | 'collection'
  | 'streak'
  | 'acorn'
  | 'social'
  | 'subscription'
  | 'onboarding'
  | 'upsell'
  | 'lifecycle'
  | 'system'
  | 'chat'
  | 'feedback'
  | 'shop'
  | 'space'

// ── All events Mori can fire ──────────────────────────────────────────────────
// Add new events here as features require them.
export type MoriEventMap = {

  // CARD EVENTS
  'card.shown': {
    userId: string
    contentLibraryId: string
    cardType: string
    instanceId: string | null
  }
  'card.tapped': {
    userId: string
    contentLibraryId: string
    cardType: string
    ctaAction: string
  }
  'card.dismissed': {
    userId: string
    contentLibraryId: string
    cardType: string
    dismissedFor: '3_months' | 'forever' | 'session'
  }
  'card.completed': {
    userId: string
    contentLibraryId: string
    cardType: string
    completionType: 'memory' | 'acknowledgement'
  }
  'card.swiped_past': {
    userId: string
    contentLibraryId: string
    cardType: string
  }

  // MEMORY EVENTS
  'memory.created': {
    userId: string
    memoryId: string
    memoryType: string
    contentLibraryId: string | null
    slot: 'first' | 'subsequent' | 'none'
  }
  'memory.story.ready': {
    userId: string
    memoryId: string
    storyLength: number
  }
  'memory.story.viewed': {
    userId: string
    memoryId: string
  }

  // CHALLENGE EVENTS
  'challenge.completed': {
    userId: string
    memoryId: string | null
    contentLibraryId: string
    completionType: 'memory' | 'acknowledgement'
    acornReward?: number
  }

  // COLLECTION EVENTS
  'collection.started': {
    userId: string
    collectionId: string
    templateId: string
  }
  'collection.completed': {
    userId: string
    collectionId: string
    templateId: string
    acornReward?: number
  }
  /** Fires when `active_until` transitions from null to a future timestamp. */
  'collection.active_mode.started': {
    userId: string
    collectionId: string
    activeUntil: string
  }
  /** Fires when `active_until` transitions back to null (manual end or expiry). */
  'collection.active_mode.ended': {
    userId: string
    collectionId: string
  }
  /** Fires when a no-template (bucket) collection is closed.
   *  Distinguished from `collection.completed`, which is template-driven. */
  'collection.ended': {
    userId: string
    collectionId: string
    templateId: string | null
  }

  // STREAK EVENTS
  'streak.updated': {
    userId: string
    currentStreak: number
    longestStreak: number
    isMilestone: boolean
    previousStreak: number
  }
  'streak.broken': {
    userId: string
    previousStreak: number
  }
  'streak.at.risk': {
    userId: string
    currentStreak: number
  }

  // ACORN EVENTS
  'acorns.awarded': {
    userId: string
    amount: number
    reason: string
    source: MoriEventCategory
    newBalance: number
  }

  // ONBOARDING EVENTS
  'onboarding.started': { userId: string }
  'onboarding.step.completed': {
    userId: string
    step: number
    stepName: string
  }
  'onboarding.completed': {
    userId: string
    durationSeconds: number
  }
  'onboarding.abandoned': {
    userId: string
    lastStep: number
  }
  /** Fires when the user taps the CTA on the last intro slide (e.g. "Tell Mori your first story"). */
  'intro.completed': {
    userId: string
  }

  // UPSELL EVENTS
  'upsell.card.shown': {
    userId: string
    cardId: string
    tier: string
  }
  'upsell.card.tapped': {
    userId: string
    cardId: string
    tier: string
  }
  'upsell.card.dismissed': {
    userId: string
    cardId: string
  }
  'upsell.completed': {
    userId: string
    tier: string
  }

  // SOCIAL EVENTS — wired when those flows are built
  'tribe.invite.sent': { userId: string }
  'tribe.invite.accepted': {
    userId: string
    invitedBy: string
  }
  'tribe.member.first.memory': {
    userId: string
    contributorId: string
    memoryId: string
  }
  /** Fired after a successful claim_referral RPC call. `userId` is the
   *  referrer (the acorn recipient) so analytics/subscribers reading
   *  payload.userId stay consistent with the rest of the event bus. */
  'referral.rewarded': {
    userId: string
    referrerId: string
    referredId: string
    amount: number
    timestamp: number
  }

  // SUBSCRIPTION EVENTS
  'subscription.upgraded': {
    userId: string
    fromTier: string
    toTier: string
  }
  'subscription.payment.failed': { userId: string }
  'subscription.payment.recovered': { userId: string }

  // LIFECYCLE EVENTS
  'app.first.open': {
    userId: string
    locale: string
    detectedLanguage: string
  }
  'child.birthday.today': {
    userId: string
    childId: string
    childName: string
    age: number
  }
  'memory.anniversary': {
    userId: string
    memoryId: string
    yearsAgo: number
  }
  'time_capsule.unlocked': {
    userId: string
    capsuleId: string
  }

  // SHARE EVENTS
  'memory.shared': {
    userId: string
    memoryId: string
    shareToken: string
    shareType: 'new' | 'reused'
  }

  // SYSTEM EVENTS
  'storage.threshold.reached': {
    userId: string
    percentUsed: number
    threshold: 80 | 100
  }
  'mori_message.created': {
    userId: string
    instanceId: string
    triggerEvent: string
  }

  // CHAT EVENTS — wired when chat feature is built
  'chat.started': {
    userId: string
    seedCardId: string | null
  }
  'chat.memory.created': {
    userId: string
    memoryId: string
    chatId: string
  }
  'chat.ended': {
    userId: string
    messageCount: number
    memoriesCreated: number
  }

  // FEEDBACK EVENTS
  'feedback.monthly_prompt': {
    userId: string
  }
  'feedback.submitted': {
    userId: string
    rating: number
    hasVoice: boolean
    durationSeconds: number
    eligible: boolean
  }

  // SPACE EVENTS
  /** Fired after the user's active_space_id has changed — whether via the
   *  space switcher, accepting an invite that flipped their active space,
   *  or creating a new owned space. Hooks that cache per-space data
   *  subscribe to refresh their state. */
  'space.switched': {
    userId: string
    spaceId: string
  }

  // SHOP EVENTS
  'shop.purchased': {
    userId: string
    walletId: string
    shopItemId: string
    itemType: 'app_feature' | 'local_voucher' | 'online_code' | 'digital_keepsake'
    acornCost: number
  }
  'shop.voucher.redeemed': {
    userId: string
    walletId: string
    shopItemId: string
  }
  'shop.code.revealed': {
    userId: string
    walletId: string
    shopItemId: string
    firstReveal: boolean
  }
}

export type MoriEventName = keyof MoriEventMap

// ── Category lookup — every event mapped to its category ─────────────────────
export const EVENT_CATEGORIES: Record<MoriEventName, MoriEventCategory> = {
  'card.shown':                    'card',
  'card.tapped':                   'card',
  'card.dismissed':                'card',
  'card.completed':                'card',
  'card.swiped_past':              'card',
  'memory.created':                'memory',
  'memory.story.ready':            'memory',
  'memory.story.viewed':           'memory',
  'memory.shared':                 'memory',
  'challenge.completed':           'challenge',
  'collection.started':            'collection',
  'collection.completed':          'collection',
  'collection.active_mode.started':'collection',
  'collection.active_mode.ended':  'collection',
  'collection.ended':              'collection',
  'streak.updated':                'streak',
  'streak.broken':                 'streak',
  'streak.at.risk':                'streak',
  'acorns.awarded':                'acorn',
  'onboarding.started':            'onboarding',
  'onboarding.step.completed':     'onboarding',
  'onboarding.completed':          'onboarding',
  'onboarding.abandoned':          'onboarding',
  'intro.completed':               'onboarding',
  'upsell.card.shown':             'upsell',
  'upsell.card.tapped':            'upsell',
  'upsell.card.dismissed':         'upsell',
  'upsell.completed':              'upsell',
  'tribe.invite.sent':             'social',
  'tribe.invite.accepted':         'social',
  'tribe.member.first.memory':     'social',
  'referral.rewarded':             'social',
  'subscription.upgraded':         'subscription',
  'subscription.payment.failed':   'subscription',
  'subscription.payment.recovered':'subscription',
  'app.first.open':                'lifecycle',
  'child.birthday.today':          'lifecycle',
  'memory.anniversary':            'lifecycle',
  'time_capsule.unlocked':         'lifecycle',
  'storage.threshold.reached':     'system',
  'mori_message.created':          'system',
  'chat.started':                  'chat',
  'chat.memory.created':           'chat',
  'chat.ended':                    'chat',
  'feedback.monthly_prompt':       'feedback',
  'feedback.submitted':            'feedback',
  'shop.purchased':                'shop',
  'shop.voucher.redeemed':         'shop',
  'shop.code.revealed':            'shop',
  'space.switched':                'space',
}

type Subscriber<T> = (payload: T) => void | Promise<void>

const subscribers = new Map<string, Set<Subscriber<unknown>>>()

export function fireEvent<E extends MoriEventName>(
  event: E,
  payload: MoriEventMap[E],
): void {
  const category = EVENT_CATEGORIES[event]
  const enriched = { ...payload, _event: event, _category: category }

  // Notify in-process subscribers
  const subs = subscribers.get(event)
  if (subs) {
    subs.forEach((fn) => {
      try {
        fn(enriched as unknown)
      } catch (err) {
        console.error(`[events] error in ${event} subscriber:`, err)
      }
    })
  }

  // Also dispatch as DOM CustomEvent for cross-component reach
  window.dispatchEvent(new CustomEvent(`mori:${event}`, { detail: enriched }))
}

export function onEvent<E extends MoriEventName>(
  event: E,
  handler: Subscriber<MoriEventMap[E]>,
): () => void {
  if (!subscribers.has(event)) {
    subscribers.set(event, new Set())
  }
  subscribers.get(event)!.add(handler as Subscriber<unknown>)

  return () => {
    subscribers.get(event)?.delete(handler as Subscriber<unknown>)
  }
}

// Subscribe to all events in a category
export function onEventCategory(
  category: MoriEventCategory,
  handler: (event: MoriEventName, payload: unknown) => void,
): () => void {
  const cleanups: (() => void)[] = []

  const eventsInCategory = (
    Object.entries(EVENT_CATEGORIES) as [MoriEventName, MoriEventCategory][]
  )
    .filter(([, cat]) => cat === category)
    .map(([event]) => event)

  eventsInCategory.forEach((event) => {
    cleanups.push(onEvent(event, (payload) => handler(event, payload)))
  })

  return () => cleanups.forEach((fn) => fn())
}

// Hook for React components to subscribe to events.
// Auto-cleans up on unmount.
export function useEvent<E extends MoriEventName>(
  event: E,
  handler: Subscriber<MoriEventMap[E]>,
  deps: DependencyList = [],
): void {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => onEvent(event, handler), deps)
}
